/**
 * Corte de carril — criterio unificado.
 *
 * El corte se registra de tres formas y las tres cuentan (decisión de producto
 * 2026-07-28: si el catálogo dice corte de carril, es un corte de carril):
 *   1. `laneCutting === true` — desplegable de Edición de Actuación / Datos del accidente.
 *   2. Actividad "Tall de carril" — solo actuaciones; `Accident` no tiene relación con `Activity`.
 *   3. OT predefinida "161-Tall de carril".
 *
 * Los ids de catálogo se resuelven UNA vez emparejando el nombre y después cada
 * fila se compara por id, no por texto. El catálogo llega ya filtrado por tenant
 * desde el backend (`getReusableArrayFilter`), así que esto es tenant-aware sin
 * trabajo extra.
 *
 * El elemento `ElementOrder` "Tall de carril" queda FUERA a propósito: se
 * verificó contra producción que no aporta ni un caso que no capturen ya los
 * tres criterios, porque la OT predefinida 161 lo lleva asociado.
 *
 * Este fichero es PURO: sin Apollo, sin React. La carga de los catálogos vive en
 * `src/gql/selectors/laneCutCatalog.ts`.
 *
 * 🔴 DESDE 2026-08-04 EL CÁLCULO LOCAL ES SOLO UN RESPALDO.
 *
 * El backend materializa el criterio en `Action.isLaneCut` / `Accident.isLaneCut`
 * y lo expone por GraphQL. La regla es `resolveIsLaneCut`: **manda el campo del
 * servidor**, y solo si no llega se calcula aquí. Así la columna y el filtro
 * leen el mismo valor y no pueden divergir (§ 5 del spec).
 *
 * El cálculo local NO se puede borrar: una fila creada o editada sin cobertura
 * vive en la caché optimista sin `isLaneCut`, y sin el respaldo se pintaría en
 * blanco hasta sincronizar. Es el mismo patrón que `viewer*`/`rolePermissions`
 * en RBAC y que `pendingActionLink ?? !linkedOrderRoid` en el RV.
 *
 * 🔴 GEMELO EN EL BACKEND: `rois-back/src/utils/lane-cut.helper.ts`. Mientras
 * exista el respaldo, el criterio sigue duplicado: si tocas `LANE_CUT_PATTERN`,
 * toca el gemelo — su test `test/unit/utils/lane-cut.unit.spec.ts` es el
 * tripwire que compara los dos patrones y los mismos casos de nombre.
 */

/**
 * Único sitio donde se decide qué nombre de catálogo "es" un corte de carril.
 * Cubre catalán y castellano porque el catálogo es dato del tenant.
 * Si esto cambia, el test `lane-cut-criterion.spec.ts` falla — es su guardián.
 */
export const LANE_CUT_PATTERN = /tall de carril|corte de carril/i;

export type LaneCutIds = {
  activityIds: Set<string>;
  predefinedOrderIds: Set<string>;
};

export const EMPTY_LANE_CUT_IDS: LaneCutIds = {
  activityIds: new Set<string>(),
  predefinedOrderIds: new Set<string>(),
};

type CatalogEntry = { id?: string | null; name?: string | null };

const matchingIds = (entries?: CatalogEntry[] | null): Set<string> =>
  new Set(
    (entries ?? [])
      .filter((e) => !!e?.id && LANE_CUT_PATTERN.test(e?.name ?? ''))
      .map((e) => e.id as string),
  );

/**
 * Del catálogo (ya filtrado por tenant) a los ids que SON corte de carril.
 * Dos conjuntos separados a propósito: cada campo se compara solo contra su
 * propio catálogo, sin que un id de actividad pueda casar contra una predefinida.
 */
export const resolveLaneCutIds = (
  activities?: CatalogEntry[] | null,
  predefinedOrders?: CatalogEntry[] | null,
): LaneCutIds => ({
  activityIds: matchingIds(activities),
  predefinedOrderIds: matchingIds(predefinedOrders),
});

export type LaneCutSource = {
  laneCutting?: boolean | null;
  activityIDs?: (string | null)[] | null;
  predefinedOrderIDs?: (string | null)[] | null;
};

/**
 * Cálculo LOCAL del criterio. Respaldo de `resolveIsLaneCut` — no lo llames
 * directo desde la UI: usa `resolveIsLaneCut`, que prefiere el valor del
 * servidor.
 */
export const isLaneCut = (row: LaneCutSource, ids: LaneCutIds): boolean => {
  if (row?.laneCutting === true) return true;
  if ((row?.activityIDs ?? []).some((id) => !!id && ids.activityIds.has(id))) {
    return true;
  }
  return (row?.predefinedOrderIDs ?? []).some(
    (id) => !!id && ids.predefinedOrderIds.has(id),
  );
};

/**
 * Opciones del filtro «Tall de carril» de las tablas.
 *
 * Selector SIMPLE, no multi-select: «Sí» y «No» son excluyentes por naturaleza
 * y el contrato del backend es un Boolean, no una lista.
 *
 * La primera opción es «Todos» con valor vacío — es la única forma de VACIAR un
 * `DropDownInput` desde la interfaz (su `clearSelected` es imperativo y solo lo
 * usa la papelera, que borra TODOS los filtros a la vez). Mismo patrón que el
 * desplegable de equipo en `OperatorsSelectionTable`.
 *
 * El placeholder del control es «Selecciona», como el de sus ocho hermanos de
 * la barra. Se probó con «Todos» de placeholder para no tener dos textos que
 * significan lo mismo, y se descartó al verlo: el control parecía MARCADO sin
 * estarlo, y cantaba al lado de los demás. Que «sin tocar» y «Todos» se lean
 * distinto es preferible a que «sin tocar» parezca una elección.
 *
 * Sin claves i18n nuevas: `common:filters.all`, `forms:actionEdition.yes` y
 * `.no` ya existen en ca/es/en.
 */
/** Valor de la opción «Todos». Cualquier cosa menos `''` (ver el comentario). */
export const LANE_CUT_ALL = 'all';

export const getLaneCutFilterOptions = (
  t: (key: string) => string,
): Array<{ label: string; value: string }> => [
  // Valor propio, NO vacío: `DropDownInput` resuelve su `defaultValue` contra
  // las opciones, y una opción con `value: ''` casaba con el «sin valor» del
  // arranque — el control aparecía MARCADO como «Todos» sin que nadie lo
  // eligiera. Con un valor propio, al entrar no casa nada y se ve el placeholder.
  { label: t('common:filters.all'), value: LANE_CUT_ALL },
  { label: t('forms:actionEdition.yes'), value: 'true' },
  { label: t('forms:actionEdition.no'), value: 'false' },
];

/**
 * Del valor del desplegable al del filtro.
 *
 * «Todos» y el vacío = sin filtrar (`undefined`). Ojo: NO se puede resolver con
 * un booleano a secas (`value === 'true'`), porque cualquier otro valor daría
 * `false` y eso filtraría por «No» en vez de no filtrar.
 */
export const laneCutSelectionToBoolean = (
  value: string | null | undefined,
): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

/** El inverso, para pintar el estado del control desde el cursor. */
export const booleanToLaneCutSelection = (
  laneCutting: boolean | undefined | null,
): string =>
  laneCutting === undefined || laneCutting === null ? '' : String(laneCutting);

/**
 * Valor que se PINTA. Backend primero, cálculo local como respaldo.
 *
 * `serverValue` es `Action.isLaneCut` / `Accident.isLaneCut`. Se comprueba
 * contra `null`/`undefined` y **no** por veracidad: `false` es una respuesta
 * legítima del servidor («no es corte de carril») y tratarla como ausente
 * mandaría a recalcular sin necesidad.
 */
export const resolveIsLaneCut = (
  serverValue: boolean | null | undefined,
  row: LaneCutSource,
  ids: LaneCutIds,
): boolean =>
  serverValue === null || serverValue === undefined
    ? isLaneCut(row, ids)
    : serverValue;

/**
 * ¿Hace falta cargar los catálogos para este lote?
 *
 * Solo si alguna fila carece del campo del servidor. Con datos ya
 * sincronizados esto ahorra las dos queries de `loadLaneCutIds`
 * (`FETCH_ACTIVITYS` + `FETCH_PREDEFINED_ORDERS`) en cada listado.
 */
export const needsLaneCutFallback = (
  rows: Array<{ isLaneCut?: boolean | null }>,
): boolean =>
  (rows ?? []).some((r) => r?.isLaneCut === null || r?.isLaneCut === undefined);
